/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-06-19
 */

// Add third-party dependencies.
import PropTypes from 'prop-types';
import { Box, Typography, Button, Divider, Grid } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import FacebookIcon from '@mui/icons-material/Facebook';
import AppleIcon from '@mui/icons-material/Apple';

/**
 * SocialLoginOptions component.
 * Provides social login buttons for alternative authentication methods.
 * @example
 * return (
 *   <SocialLoginOptions onSocialLogin={handleSocialLogin} />
 * )
 * @returns {React.ReactElement} component.
 */
const SocialLoginOptions = ({ onSocialLogin, showDivider }) => {
  /**
   * Handle social login button click.
   * @param {string} provider - The social login provider.
   * @returns {void}
   */
  const handleSocialLoginClick = (provider) => {
    if (onSocialLogin) {
      onSocialLogin(provider);
    } else {
      console.info(`Social login with ${provider} clicked, but no handler provided.`);
    }
  };

  // Render component.
  return (
    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
      {showDivider && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mx: 2 }}>
            OR
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        Continue with
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={() => handleSocialLoginClick('google')}
            aria-label="Sign in with Google"
            sx={{
              borderColor: '#DADCE0',
              color: 'text.primary',
              '&:hover': {
                borderColor: '#4285F4',
                backgroundColor: 'rgba(66, 133, 244, 0.04)',
              },
            }}
          >
            Google
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FacebookIcon />}
            onClick={() => handleSocialLoginClick('facebook')}
            aria-label="Sign in with Facebook"
            sx={{
              borderColor: '#DADCE0',
              color: 'text.primary',
              '&:hover': {
                borderColor: '#1877F2',
                backgroundColor: 'rgba(24, 119, 242, 0.04)',
              },
            }}
          >
            Facebook
          </Button>
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AppleIcon />}
            onClick={() => handleSocialLoginClick('apple')}
            aria-label="Sign in with Apple"
            sx={{
              borderColor: '#DADCE0',
              color: 'text.primary',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Apple
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

// Set component property types.
SocialLoginOptions.propTypes = {
  onSocialLogin: PropTypes.func,
  showDivider: PropTypes.bool,
};

// Set component default properties.
SocialLoginOptions.defaultProps = {
  onSocialLogin: null,
  showDivider: true,
};

// Export component.
export default SocialLoginOptions;
